<template>
    <v-dialog
        :model-value="visible"
        persistent
        scrollable
        max-width="750"
        @update:model-value="$emit('update:visible', $event)"
    >
        <v-card class="transport-order-modal d-flex flex-column overflow-hidden">
            <v-form ref="formRef" class="d-flex flex-column flex-grow-1 overflow-hidden min-h-0">
                <div class="flex-shrink-0 px-4 pt-4">
                    <DividerTitle :title="$t('transportOrder.sectionTransportOrder')" />
                    <v-text-field
                        v-model="formData.transport_order_number"
                        :label="$t('transportOrder.transportOrderNumber')"
                        variant="outlined"
                        density="compact"
                        hide-details="auto"
                        :rules="driverFieldRules"
                    />
                </div>
                <v-card-text class="pt-0 flex-grow-1 overflow-y-auto min-h-0">
                    <v-row dense align="start">
                        <v-col v-for="addressSection in addressSections" :key="addressSection.sectionId" cols="12">
                            <DividerTitle :title="addressSection.title" />
                            <v-row dense align="start" v-auto-animate>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="addressSection.addressFields.company_name"
                                        :label="$t('transportOrder.addressCompanyName')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="addressFieldMaxLength"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="addressSection.addressFields.street"
                                        :label="$t('transportOrder.addressStreet')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="addressFieldMaxLength"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="addressSection.addressFields.postal_code"
                                        :label="$t('transportOrder.addressPostalCode')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="addressFieldMaxLength"
                                    />
                                </v-col>
                            </v-row>
                            <v-row dense align="start" v-auto-animate>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model="addressSection.addressFields.city"
                                        :label="$t('transportOrder.addressCity')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="addressFieldMaxLength"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model="addressSection.addressFields.country"
                                        :label="$t('transportOrder.addressCountry')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="addressFieldMaxLength"
                                    />
                                </v-col>
                            </v-row>
                            <v-row dense align="start" class="mt-1">
                                <v-col cols="12">
                                    <span class="text-subtitle-2">{{ $t(addressSection.scheduledLabelKey) }}</span>
                                </v-col>
                                <v-col cols="12" md="6" class="py-1">
                                    <FormDate
                                        v-if="addressSection.sectionId === 'pickup'"
                                        :ref="
                                            (componentInstance) =>
                                                setTransportOrderScheduleDateRef('pickup', componentInstance)
                                        "
                                        v-model="transportOrderScheduleLoadingDate"
                                        data-cy-prefix="transport-order-loading-date"
                                        single
                                        density="compact"
                                        :min="loadingDateToleranceBounds.minDateYmd"
                                        :max="loadingDateToleranceBounds.maxDateYmd"
                                    />
                                    <FormDate
                                        v-else
                                        :ref="
                                            (componentInstance) =>
                                                setTransportOrderScheduleDateRef('delivery', componentInstance)
                                        "
                                        v-model="transportOrderScheduleUnloadingDate"
                                        data-cy-prefix="transport-order-unloading-date"
                                        single
                                        density="compact"
                                        :min="unloadingDateToleranceBounds.minDateYmd"
                                        :max="unloadingDateToleranceBounds.maxDateYmd"
                                    />
                                </v-col>
                                <v-col cols="12" md="6" class="py-1">
                                    <FormTime
                                        v-if="addressSection.sectionId === 'pickup'"
                                        v-model="transportOrderScheduleLoadingTime"
                                        data-cy-prefix="transport-order-loading-time"
                                        required
                                        density="compact"
                                    />
                                    <FormTime
                                        v-else
                                        v-model="transportOrderScheduleUnloadingTime"
                                        data-cy-prefix="transport-order-unloading-time"
                                        required
                                        density="compact"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="12">
                            <v-textarea
                                ref="scheduleReasonNotesRef"
                                v-model="scheduleNotes"
                                variant="outlined"
                                density="compact"
                                rows="2"
                                hide-details="auto"
                                :label="$t('transportOrder.scheduleChangeReasonLabel')"
                                :rules="scheduleReasonNotesRules"
                            />
                        </v-col>

                        <v-col cols="12">
                            <DividerTitle :title="$t('transportOrder.sectionVehicleDriver')" />
                            <v-row dense align="start" v-auto-animate>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="formData.driver_name"
                                        :label="$t('transportOrder.driverName')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="driverFieldRules"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="formData.driver_surname"
                                        :label="$t('transportOrder.driverSurname')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="driverFieldRules"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="formData.car_number"
                                        :label="$t('transportOrder.vehiclePlate')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :maxlength="carNumberMaxLength"
                                        :counter="carNumberMaxLength"
                                        :rules="driverFieldRules"
                                    />
                                </v-col>
                                <v-col cols="12" md="4" class="py-1">
                                    <v-text-field
                                        v-model="formData.customs"
                                        :label="$t('transportOrder.customs')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="12">
                            <DividerTitle :title="cargoSectionTitle" />
                            <v-row dense align="start" v-auto-animate>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model.number="formData.weight"
                                        :label="$t('transportOrder.weight')"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col v-if="hasVolume" cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model.number="formData.volume"
                                        :label="$t('transportOrder.volume')"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model.number="formData.mdp"
                                        :label="$t('transportOrder.mdp')"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-text-field
                                        v-model.number="formData.price"
                                        :label="priceFieldLabel"
                                        type="number"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-select
                                        v-model="formData.currency"
                                        :items="currencyItems"
                                        :label="$t('transportOrder.currency')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="currencyRules"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1 d-flex align-center">
                                    <v-checkbox
                                        v-model="formData.vat_type"
                                        :label="vatLabel"
                                        hide-details="auto"
                                        density="compact"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-select
                                        v-model="formData.payment_term_code"
                                        :items="paymentTermSelectItems"
                                        item-title="text"
                                        item-value="value"
                                        :label="$t('transportOrder.paymentTerm')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                                <v-col v-if="isWare" cols="12" sm="6" md="4" class="py-1">
                                    <v-select
                                        ref="platformTypeRef"
                                        v-model="formData.platform_type"
                                        :items="platformTypeSelectItems"
                                        item-title="text"
                                        item-value="value"
                                        :label="$t('formFields.platformType')"
                                        multiple
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="[platformTypeRule]"
                                    />
                                </v-col>
                                <v-col v-if="isTruck" cols="12" sm="6" md="4" class="py-1">
                                    <v-select
                                        ref="truckTypeRef"
                                        v-model="formData.truck_type"
                                        :items="truckTypeSelectItems"
                                        item-title="text"
                                        item-value="value"
                                        :label="$t('formFields.truckType')"
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                        :rules="[truckTypeRule]"
                                    />
                                </v-col>
                                <v-col cols="12" sm="6" md="4" class="py-1">
                                    <v-select
                                        v-model="formData.truck_special_features"
                                        :items="truckSpecialFeatureSelectItems"
                                        item-title="text"
                                        item-value="value"
                                        :label="$t('formFields.specialFeatures')"
                                        multiple
                                        clearable
                                        variant="outlined"
                                        density="compact"
                                        hide-details="auto"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>

                        <v-col cols="12">
                            <DividerTitle :title="$t('transportOrder.observations')" />
                            <v-textarea
                                v-model="formData.observations"
                                variant="outlined"
                                density="compact"
                                rows="2"
                                hide-details="auto"
                            />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-form>
            <v-card-actions class="flex-shrink-0 bg-surface px-4 pb-4 pt-3">
                <v-row dense class="w-100 ma-0">
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="error" @click="$emit('update:visible', false)">
                            {{ $t("reservation.cancel") }}
                        </v-btn>
                    </v-col>
                    <v-col cols="6" class="pa-1">
                        <v-btn block variant="flat" color="primary" :loading="saving" @click="onSave">
                            {{ $t("transportOrder.save") }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import DividerTitle from "@/components/forms/DividerTitle.vue";
import FormDate from "@/components/forms/FormDate.vue";
import FormTime from "@/components/forms/FormTime.vue";
import {
    PAYMENT_DEADLINE,
    TRUCK_SPECIAL_FEATURE,
    getPlatformTypesInDisplayOrder,
    getTruckTypesInDisplayOrder,
} from "@/enums/truckEnums";
import {
    ADDRESS_FIELD_MAX_LENGTH,
    CAR_NUMBER_MAX_LENGTH,
    calendarToleranceBoundsAroundAnchorIso,
    formatToHhMm,
    formatToYmd,
} from "@/utils";

const CURRENCY_ITEMS = ["EUR", "RON"];

const EMPTY_ADDRESS_FIELDS = {
    company_name: "",
    street: "",
    postal_code: "",
    city: "",
    country: "",
};

export default {
    components: { DividerTitle, FormDate, FormTime },
    props: {
        visible: Boolean,
        reservation: { type: Object, default: null },
        entryType: { type: String, required: true },
        fieldValues: { type: Object, default: null },
    },
    emits: ["update:visible", "save"],
    data() {
        return {
            saving: false,
            carNumberMaxLength: CAR_NUMBER_MAX_LENGTH,
            addressFieldMaxLength: ADDRESS_FIELD_MAX_LENGTH,
            formData: {
                transport_order_number: "",
                driver_name: "",
                driver_surname: "",
                car_number: "",
                customs: "",
                observations: "",
                weight: null,
                volume: null,
                mdp: null,
                price: null,
                currency: "",
                vat_type: false,
                payment_term_code: 1,
                distance_km: null,
                duration_text: "",
                pickup_address: { ...EMPTY_ADDRESS_FIELDS },
                delivery_address: { ...EMPTY_ADDRESS_FIELDS },
                platform_type: [],
                truck_special_features: [],
                truck_type: null,
            },
            currencyItems: CURRENCY_ITEMS,
            scheduleNotes: "",
            transportOrderScheduleDateRefs: {
                pickup: null,
                delivery: null,
            },
            transportOrderScheduleLoadingDate: {
                date: [],
                dateStart: "",
                dateEnd: "",
            },
            transportOrderScheduleLoadingTime: {
                timeStart: "",
                timeEnd: "",
            },
            transportOrderScheduleUnloadingDate: {
                date: [],
                dateStart: "",
                dateEnd: "",
            },
            transportOrderScheduleUnloadingTime: {
                timeStart: "",
                timeEnd: "",
            },
            /** Snapshot when modal loads (after GET merge); used to detect PDF schedule edits. */
            initialTransportOrderScheduleSnapshot: null,
            transportOrderScheduleBaselineCaptured: false,
        };
    },
    computed: {
        paymentTermSelectItems() {
            return PAYMENT_DEADLINE.map((term) => ({
                ...term,
                text: `${term.text} ${this.$t("formFields.days")}`,
            }));
        },
        isTruck() {
            return this.entryType === "truck";
        },
        isWare() {
            return this.entryType === "ware";
        },
        platformTypeSelectItems() {
            return getPlatformTypesInDisplayOrder().map((item) => ({
                value: item.value,
                text: this.$t(`transport.platformType.${item.value}`) || item.text,
            }));
        },
        truckTypeSelectItems() {
            return getTruckTypesInDisplayOrder().map((item) => ({
                value: item.value,
                text: this.$t(`transport.truckType.${item.value}`) || item.text,
            }));
        },
        truckSpecialFeatureSelectItems() {
            return Object.values(TRUCK_SPECIAL_FEATURE).map((item) => ({
                value: item.value,
                text: this.$t(`transport.truckSpecialFeature.${item.value}`) || item.text,
            }));
        },
        platformTypeRule() {
            return (value) =>
                (Array.isArray(value) && value.length > 0) || this.$t("validation.required");
        },
        truckTypeRule() {
            return (value) =>
                (value != null && value !== "") || this.$t("validation.required");
        },
        cargoSectionTitle() {
            return this.isTruck ? this.$t("transportOrder.sectionCargoDetails") : this.$t("transportOrder.sectionWareDetails");
        },
        priceFieldLabel() {
            return this.isTruck ? this.$t("formFields.pricePerKm") : this.$t("transportOrder.price");
        },
        transportOrderGenerateToleranceDays() {
            const period = this.reservation?.transport_order_generate_time_period;
            const parsed = Number.parseInt(String(period), 10);
            return Number.isFinite(parsed) && parsed >= 0 ? parsed : 10;
        },
        loadingDateToleranceBounds() {
            const reservationData = this.reservation || {};
            const fieldValues = this.fieldValues || {};
            const anchor =
                reservationData.chosen_loading_date ||
                fieldValues.transport_order_loading_date_iso ||
                "";
            return calendarToleranceBoundsAroundAnchorIso(anchor, this.transportOrderGenerateToleranceDays);
        },
        unloadingDateToleranceBounds() {
            const reservationData = this.reservation || {};
            const fieldValues = this.fieldValues || {};
            const anchor =
                reservationData.chosen_unloading_date ||
                fieldValues.transport_order_unloading_date_iso ||
                "";
            return calendarToleranceBoundsAroundAnchorIso(anchor, this.transportOrderGenerateToleranceDays);
        },
        /**
         * Pickup / delivery blocks: address lines plus embedded schedule editors (replacing legacy readonly scheduled lines).
         */
        addressSections() {
            return [
                {
                    sectionId: "pickup",
                    title: this.$t("transportOrder.sectionPickupAddress"),
                    addressFields: this.formData.pickup_address,
                    scheduledLabelKey: "transportOrder.scheduledPickup",
                },
                {
                    sectionId: "delivery",
                    title: this.$t("transportOrder.sectionDeliveryAddress"),
                    addressFields: this.formData.delivery_address,
                    scheduledLabelKey: "transportOrder.scheduledDelivery",
                },
            ];
        },
        driverFieldRules() {
            return [(v) => !!String(v ?? "").trim() || this.$t("validation.required")];
        },
        hasVolume() {
            const volumeFromFieldValues = this.fieldValues?.volume;
            return volumeFromFieldValues != null && volumeFromFieldValues !== "";
        },
        currencyRules() {
            return [(v) => !!String(v || "").trim() || this.$t("validation.required")];
        },
        vatLabel() {
            return this.formData?.vat_type === true
                ? this.$t("formFields.withVAT")
                : this.$t("formFields.withoutVAT");
        },
        transportOrderScheduleWasModifiedFromBaseline() {
            const baseline = this.initialTransportOrderScheduleSnapshot;
            if (!baseline) return false;
            const current = this.buildTransportOrderScheduleSnapshot();
            return (
                baseline.loadingDateIso !== current.loadingDateIso ||
                baseline.unloadingDateIso !== current.unloadingDateIso ||
                baseline.loadingTimeStart !== current.loadingTimeStart ||
                baseline.loadingTimeEnd !== current.loadingTimeEnd ||
                baseline.unloadingTimeStart !== current.unloadingTimeStart ||
                baseline.unloadingTimeEnd !== current.unloadingTimeEnd
            );
        },
        scheduleReasonNotesRules() {
            return [
                () => {
                    if (!this.transportOrderScheduleWasModifiedFromBaseline) return true;
                    const trimmedNotes = String(this.scheduleNotes || "").trim();
                    return (
                        trimmedNotes.length > 0 || this.$t("transportOrder.scheduleChangeReasonRequired")
                    );
                },
            ];
        },
    },
    watch: {
        visible(isVisible) {
            if (isVisible) {
                this.scheduleNotes = "";
                this.transportOrderScheduleBaselineCaptured = false;
                this.initialTransportOrderScheduleSnapshot = null;
                this.$nextTick(() => this.syncScheduleModelsFromSources());
            } else {
                this.transportOrderScheduleDateRefs.pickup = null;
                this.transportOrderScheduleDateRefs.delivery = null;
                this.transportOrderScheduleBaselineCaptured = false;
                this.initialTransportOrderScheduleSnapshot = null;
            }
        },
        "reservation.uid"() {
            this.transportOrderScheduleBaselineCaptured = false;
            this.initialTransportOrderScheduleSnapshot = null;
            this.syncScheduleModelsFromSources();
        },
        fieldValues: {
            immediate: true,
            handler(incomingFieldValues) {
                if (!incomingFieldValues) return;

                this.formData.transport_order_number =
                    incomingFieldValues.transport_order_number != null
                        ? String(incomingFieldValues.transport_order_number).trim()
                        : "";
                this.formData.driver_name = incomingFieldValues.driver_name || "";
                this.formData.driver_surname = incomingFieldValues.driver_surname || "";
                this.formData.car_number = incomingFieldValues.car_number || "";
                this.formData.customs = incomingFieldValues.customs != null ? String(incomingFieldValues.customs) : "";
                this.formData.observations =
                    incomingFieldValues.notes != null && incomingFieldValues.notes !== undefined
                        ? String(incomingFieldValues.notes)
                        : "";
                this.formData.weight = incomingFieldValues.weight != null ? incomingFieldValues.weight : null;
                this.formData.volume = incomingFieldValues.volume != null ? incomingFieldValues.volume : null;
                this.formData.mdp = incomingFieldValues.mdp != null ? incomingFieldValues.mdp : null;
                this.formData.price = incomingFieldValues.price != null ? incomingFieldValues.price : null;
                this.formData.currency = incomingFieldValues.currency || "";
                this.formData.vat_type = incomingFieldValues.vat_type === true;
                const ptc = incomingFieldValues.payment_term_code;
                this.formData.payment_term_code =
                    ptc != null && ptc !== "" && !Number.isNaN(Number(ptc)) ? Number(ptc) : 1;
                this.formData.distance_km = incomingFieldValues.distance_km != null ? incomingFieldValues.distance_km : null;
                this.formData.duration_text = incomingFieldValues.duration_text || "";
                this.formData.pickup_address = this.mergePickupDeliveryFromApi(
                    incomingFieldValues,
                    "pickup_address",
                    "loading_location"
                );
                this.formData.delivery_address = this.mergePickupDeliveryFromApi(
                    incomingFieldValues,
                    "delivery_address",
                    "unloading_location"
                );
                this.formData.platform_type = Array.isArray(incomingFieldValues.platform_type)
                    ? [...incomingFieldValues.platform_type]
                    : [];
                this.formData.truck_special_features = Array.isArray(incomingFieldValues.truck_special_features)
                    ? [...incomingFieldValues.truck_special_features]
                    : [];
                const tt = incomingFieldValues.truck_type;
                this.formData.truck_type =
                    tt != null && tt !== "" && !Number.isNaN(Number(tt)) ? Number(tt) : null;

                this.syncScheduleModelsFromSources();
            },
        },
    },
    methods: {
        setTransportOrderScheduleDateRef(sectionId, componentInstance) {
            if (sectionId !== "pickup" && sectionId !== "delivery") return;
            this.transportOrderScheduleDateRefs[sectionId] = componentInstance || null;
        },
        buildTransportOrderScheduleSnapshot() {
            const loadingDateIso =
                formatToYmd(this.transportOrderScheduleLoadingDate?.date?.[0]) ||
                this.transportOrderScheduleLoadingDate?.dateStart ||
                "";
            const unloadingDateIso =
                formatToYmd(this.transportOrderScheduleUnloadingDate?.date?.[0]) ||
                this.transportOrderScheduleUnloadingDate?.dateStart ||
                "";
            const normalizeTimeSegment = (timeValue) => String(timeValue || "").trim().substring(0, 5);
            return {
                loadingDateIso,
                unloadingDateIso,
                loadingTimeStart: normalizeTimeSegment(this.transportOrderScheduleLoadingTime?.timeStart),
                loadingTimeEnd: normalizeTimeSegment(this.transportOrderScheduleLoadingTime?.timeEnd),
                unloadingTimeStart: normalizeTimeSegment(this.transportOrderScheduleUnloadingTime?.timeStart),
                unloadingTimeEnd: normalizeTimeSegment(this.transportOrderScheduleUnloadingTime?.timeEnd),
            };
        },
        isoPrefixOrEmpty(value) {
            if (value == null || value === "") return "";
            const text = String(value).trim();
            return text.length >= 10 ? text.slice(0, 10) : "";
        },
        singleDateModelFromIso(isoPrefix) {
            if (!isoPrefix || isoPrefix.length < 10) {
                return { date: [], dateStart: "", dateEnd: "" };
            }
            const dateObject = new Date(`${isoPrefix}T12:00:00`);
            if (Number.isNaN(dateObject.getTime())) {
                return { date: [], dateStart: "", dateEnd: "" };
            }
            return {
                date: [dateObject],
                dateStart: isoPrefix,
                dateEnd: isoPrefix,
            };
        },
        syncScheduleModelsFromSources() {
            const incomingFieldValues = this.fieldValues;
            const reservationData = this.reservation || {};
            if (!incomingFieldValues && !reservationData.uid) return;

            const fieldValues = incomingFieldValues || {};
            const loadingIso =
                this.isoPrefixOrEmpty(fieldValues.transport_order_loading_date_iso) ||
                this.isoPrefixOrEmpty(reservationData.chosen_loading_date);
            const unloadingIso =
                this.isoPrefixOrEmpty(fieldValues.transport_order_unloading_date_iso) ||
                this.isoPrefixOrEmpty(reservationData.chosen_unloading_date);

            this.transportOrderScheduleLoadingDate = this.singleDateModelFromIso(loadingIso);
            this.transportOrderScheduleUnloadingDate = this.singleDateModelFromIso(unloadingIso);

            const loadingLocation = fieldValues.loading_location || {};
            const unloadingLocation = fieldValues.unloading_location || {};
            const loadingStart =
                formatToHhMm(loadingLocation.time_start) ||
                formatToHhMm(reservationData.chosen_loading_time_start) ||
                "";
            const loadingEnd =
                formatToHhMm(loadingLocation.time_end) ||
                formatToHhMm(reservationData.chosen_loading_time_end) ||
                "";
            const unloadingStart =
                formatToHhMm(unloadingLocation.time_start) ||
                formatToHhMm(reservationData.chosen_unloading_time_start) ||
                "";
            const unloadingEnd =
                formatToHhMm(unloadingLocation.time_end) ||
                formatToHhMm(reservationData.chosen_unloading_time_end) ||
                "";

            this.transportOrderScheduleLoadingTime = {
                timeStart: loadingStart || "",
                timeEnd: loadingEnd || "",
            };
            this.transportOrderScheduleUnloadingTime = {
                timeStart: unloadingStart || "",
                timeEnd: unloadingEnd || "",
            };

            if (this.visible && !this.transportOrderScheduleBaselineCaptured) {
                this.initialTransportOrderScheduleSnapshot = this.buildTransportOrderScheduleSnapshot();
                this.transportOrderScheduleBaselineCaptured = true;
            }
        },
        /**
         * Prefill pickup/delivery: saved reservation address lines first; empty slots from
         * loading_location / unloading_location (always rebuilt on GET from build_transport_order_data).
         */
        mergePickupDeliveryFromApi(fieldValues, addressPayloadKey, entryLocationKey) {
            const addressFromReservation = fieldValues?.[addressPayloadKey] || {};
            const entryLocation = fieldValues?.[entryLocationKey] || {};
            return {
                company_name: this.normalizeOptionalText(addressFromReservation.company_name),
                street: this.normalizeOptionalText(addressFromReservation.street),
                postal_code:
                    this.normalizeOptionalText(addressFromReservation.postal_code) ||
                    this.normalizeOptionalText(entryLocation.postal_code),
                city: this.normalizeOptionalText(addressFromReservation.city) || this.normalizeOptionalText(entryLocation.city),
                country:
                    this.normalizeOptionalText(addressFromReservation.country) ||
                    this.countryDisplayFromEntryLocation(entryLocation),
            };
        },
        normalizeOptionalText(value) {
            if (value == null || value === "") return "";
            return String(value).trim();
        },
        /** `country_name` and ISO `country` come from the API (entry location); no client-side lookup. */
        countryDisplayFromEntryLocation(entryLocation) {
            if (!entryLocation) return "";
            const localizedName = this.normalizeOptionalText(entryLocation.country_name);
            if (localizedName) return localizedName;
            return this.normalizeOptionalText(entryLocation.country);
        },
        async onSave() {
            const validationResult = await this.$refs.formRef?.validate();
            if (!validationResult?.valid) return;

            const pickupDateComponent = this.transportOrderScheduleDateRefs.pickup;
            const deliveryDateComponent = this.transportOrderScheduleDateRefs.delivery;
            const loadingDateOk = pickupDateComponent?.validateFields
                ? await pickupDateComponent.validateFields()
                : true;
            const unloadingDateOk = deliveryDateComponent?.validateFields
                ? await deliveryDateComponent.validateFields()
                : true;
            if (!loadingDateOk || !unloadingDateOk) return;

            const loadingIsoPayload = formatToYmd(
                this.transportOrderScheduleLoadingDate?.date?.[0] ||
                    this.transportOrderScheduleLoadingDate?.dateStart
            );
            const unloadingIsoPayload = formatToYmd(
                this.transportOrderScheduleUnloadingDate?.date?.[0] ||
                    this.transportOrderScheduleUnloadingDate?.dateStart
            );
            if (
                !loadingIsoPayload ||
                !unloadingIsoPayload ||
                !this.transportOrderScheduleLoadingTime?.timeStart ||
                !this.transportOrderScheduleLoadingTime?.timeEnd ||
                !this.transportOrderScheduleUnloadingTime?.timeStart ||
                !this.transportOrderScheduleUnloadingTime?.timeEnd
            ) {
                return;
            }

            const payload = {
                transport_order_number: String(this.formData.transport_order_number || "").trim(),
                driver_name: this.formData.driver_name,
                driver_surname: this.formData.driver_surname,
                car_number: this.formData.car_number,
                customs: String(this.formData.customs || "").trim(),
                observations: this.formData.observations,
                weight: this.formData.weight,
                volume: this.formData.volume,
                mdp: this.formData.mdp,
                price: this.formData.price,
                currency: this.formData.currency,
                vat_type: this.formData.vat_type === true,
                payment_term_code: this.formData.payment_term_code,
                distance_km: this.formData.distance_km,
                duration_text: this.formData.duration_text,
                pickup_address: { ...this.formData.pickup_address },
                delivery_address: { ...this.formData.delivery_address },
                truck_special_features: [...(this.formData.truck_special_features || [])],
            };
            if (this.isWare) {
                payload.platform_type = [...(this.formData.platform_type || [])];
            }
            if (this.isTruck) {
                payload.truck_type = this.formData.truck_type;
            }
            payload.transport_order_loading_date_iso = loadingIsoPayload;
            payload.transport_order_unloading_date_iso = unloadingIsoPayload;
            payload.loading_time_start = this.transportOrderScheduleLoadingTime.timeStart;
            payload.loading_time_end = this.transportOrderScheduleLoadingTime.timeEnd;
            payload.unloading_time_start = this.transportOrderScheduleUnloadingTime.timeStart;
            payload.unloading_time_end = this.transportOrderScheduleUnloadingTime.timeEnd;
            payload.transport_order_schedule_notes = (this.scheduleNotes || "").trim();

            this.$emit("save", payload);
        },
        setSaving(isSaving) {
            this.saving = isSaving;
        },
    },
};
</script>

<style scoped>
/* Tighter section spacing than global publish form (20px); align divider with field columns (no extra horizontal inset). */
.transport-order-modal :deep(.v-divider__wrapper) {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.transport-order-modal :deep(.v-divider__content) {
    padding-left: 0 !important;
    padding-right: 0 !important;
}
</style>
